/**
 * Sound preference store (BUILD_SPEC.md §16.1).
 *
 * This is the *per-device* runtime source of truth for the three
 * playback rules the dashboard observes: master mute, per-keyword
 * enable, volume. Each kiosk persists its own copy in localStorage so
 * staff can mute the room TV without admin permissions while the
 * office-wide defaults set in `/admin/settings` remain authoritative
 * for the policy of record (audited, in the Setting table).
 *
 * The store intentionally does NOT mirror the server settings at
 * runtime. Cross-device propagation of preference changes is not in
 * scope — the spec's notification model is "play a tone when something
 * changed on *this* display," not "synchronize a fleet."
 *
 * Defaults align with the §21 step 56 acceptance criteria and the
 * server seed (sound_*_enabled = true, sound_volume = 60) so a fresh
 * install plays sounds out of the box.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { KeywordType } from "../../../../packages/keywords/index.js";

interface SoundState {
  enabled: boolean;
  /** Per-keyword toggles. Master mute trumps these. */
  perKeyword: Record<KeywordType, boolean>;
  /** 0–100 integer; maps to HTMLMediaElement.volume / 100. */
  volume: number;
  setEnabled: (enabled: boolean) => void;
  toggle: () => void;
  setPerKeyword: (keyword: KeywordType, enabled: boolean) => void;
  setVolume: (volume: number) => void;
}

export const useSoundStore = create<SoundState>()(
  persist(
    (set, get) => ({
      // Defaults match the server seed: an unconfigured kiosk produces
      // audible notifications, which is the §16.1 expectation for an
      // office TV. Staff can flip the header toggle to mute locally.
      enabled: true,
      perKeyword: { APPROVAL: true, ASSIGNMENT: true, REJECTION: true },
      volume: 60,
      setEnabled: (enabled) => set({ enabled }),
      toggle: () => set({ enabled: !get().enabled }),
      setPerKeyword: (keyword, enabled) =>
        set({ perKeyword: { ...get().perKeyword, [keyword]: enabled } }),
      setVolume: (volume) =>
        set({ volume: Math.max(0, Math.min(100, Math.round(volume))) }),
    }),
    {
      name: "azzam.sound",
      version: 2,
      // Bumped from v1 (which only had `enabled`) to v2 (added
      // perKeyword + volume). For an existing kiosk with v1 state,
      // merge in the new defaults so the missing keys don't render
      // the store unusable.
      migrate: (persistedState, fromVersion) => {
        const state = (persistedState ?? {}) as Partial<SoundState>;
        if (fromVersion < 2) {
          return {
            enabled: state.enabled ?? true,
            perKeyword: { APPROVAL: true, ASSIGNMENT: true, REJECTION: true },
            volume: 60,
          } as Partial<SoundState>;
        }
        return state as Partial<SoundState>;
      },
    },
  ),
);
